import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    closeMenu();
  };

  return (
    <>
      <header className={`fixed top-0 left-0 w-full z-[1000] py-4 border-b transition-all duration-200 ${
        scrolled 
          ? 'bg-zinc-900/95 border-zinc-800/50' 
          : 'bg-zinc-900/80 border-transparent'
      } backdrop-blur-xl`}>
        <div className="max-w-[1300px] mx-auto px-8 flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => scrollToSection('hero')}>
            <h1 className="font-display text-[1.75rem] md:text-3xl text-white font-bold tracking-tight">
              <span className="text-crimson">L</span>einen <span className="text-crimson">L</span>uder
            </h1>
          </div>
          
          <nav className={`md:block ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          } fixed md:relative top-0 right-0 h-screen md:h-auto w-[320px] max-w-[85%] md:w-auto md:max-w-none bg-zinc-900/98 md:bg-transparent pt-20 md:pt-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] md:translate-x-0 backdrop-blur-xl md:backdrop-blur-none shadow-[-10px_0_40px_rgba(0,0,0,0.6)] md:shadow-none border-l border-zinc-800 md:border-0 before:absolute before:top-0 before:left-0 before:w-[3px] before:h-full before:bg-gradient-to-b before:from-crimson before:via-red-500 before:to-crimson md:before:hidden`}>
            <ul className="flex flex-col md:flex-row gap-0 md:gap-8 list-none m-0 p-0 px-6 md:px-0">
              {[
                { id: 'hero', label: 'Home' },
                { id: 'services', label: 'Speisekarte' },
                { id: 'projects', label: 'Seilwerke' },
                { id: 'about', label: 'About Me' },
                { id: 'contact', label: 'Contact' }
              ].map((item, index) => (
                <li 
                  key={item.id}
                  className={`w-full md:w-auto transition-all duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-[30px] md:opacity-100 md:translate-x-0'
                  }`}
                  style={{ transitionDelay: isOpen ? `${0.1 + index * 0.05}s` : '0s' }}
                >
                  <button 
                    onClick={() => scrollToSection(item.id)}
                    className="group relative w-full md:w-auto text-left md:text-center font-medium text-[1.1rem] md:text-sm text-zinc-400 px-5 md:px-4 py-4 md:py-2 rounded-lg md:rounded-none transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-crimson/10 md:hover:bg-transparent hover:text-white hover:pl-6 md:hover:pl-4 hover:text-crimson bg-none border-0 cursor-pointer before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-[60%] before:bg-crimson before:rounded before:scale-x-0 before:origin-left before:transition-transform before:duration-300 hover:before:scale-x-100 md:before:hidden"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
            <a 
              href="mailto:leinenluder@web.de?subject=Anfrage%20über%20Website&body=Hallo%20Leinenluder-Team,%0D%0A%0D%0A" 
              onClick={closeMenu}
              className={`md:hidden flex items-center justify-center gap-3 px-7 py-[1.1rem] mx-6 mt-8 mb-4 bg-crimson text-white border-2 border-crimson rounded-lg text-[0.95rem] font-semibold tracking-tight no-underline transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-transparent hover:text-crimson hover:-translate-y-0.5 ${
                isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              } [transition-delay:0.4s]`}
            >
              <Send size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              <span>E-Mail schreiben</span>
            </a>
          </nav>
          
          <div 
            className={`md:hidden flex flex-col justify-center items-center cursor-pointer gap-[5px] p-2.5 border-0 bg-transparent relative z-[1001] w-10 h-10`}
            onClick={toggleMenu}
          >
            <span className={`block w-6 h-0.5 rounded transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              isOpen 
                ? 'rotate-45 translate-x-[6px] translate-y-[6px] bg-crimson' 
                : 'bg-white hover:bg-crimson'
            }`}></span>
            <span className={`block w-6 h-0.5 rounded transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              isOpen 
                ? 'opacity-0 scale-0' 
                : 'bg-white hover:bg-crimson'
            }`}></span>
            <span className={`block w-6 h-0.5 rounded transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              isOpen 
                ? '-rotate-45 translate-x-[6px] -translate-y-[6px] bg-crimson' 
                : 'bg-white hover:bg-crimson'
            }`}></span>
          </div>
        </div>
      </header>
      
      {isOpen && (
        <div 
          className="fixed top-0 left-0 w-full h-screen bg-black/80 backdrop-blur-sm z-[999] animate-[fadeIn_0.4s_cubic-bezier(0.16,1,0.3,1)]"
          onClick={closeMenu}
        ></div>
      )}
    </>
  );
};

export default Header;

